import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import { appointmentDraftActions, appointmentDraftSelectors, timeSlotsApi } from '~/api';
import { servicesSelectors } from '~/selectors';
import {
  envConfigSelectors,
  loadingSpinnerActions,
  Messages,
  messagesActions,
  errorTypes,
  infoTypes,
  errorMessagesActions,
} from '~/components';
import { TimeSlots } from '~/components/calendar-with-filters/time-slots';
import { areSlotsEqual } from '~/components/calendar-with-filters/time-slots/helpers';
import { getRandomNumber } from '~/utils';

export class TimeSlotsContainer extends PureComponent {
  state = {
    appointmentTypeSlots: {},
    timeSlotsAreAvailable: true,
  };

  componentDidMount() {
    const { dispatchCountedApiCallFinished, dispatchClearMessages, selectedDate } = this.props;

    if (selectedDate) {
      dispatchCountedApiCallFinished(false);
      this.loadTimes(selectedDate)
        .then((appointmentTypeSlots) => {
          this.setState(() => ({
            appointmentTypeSlots,
          }));
          dispatchCountedApiCallFinished(true);
          return appointmentTypeSlots;
        })
        .then((appointmentTypeSlots) => {
          const timeSlotsAreAvailable = this.checkTimeSlotsAvailability(appointmentTypeSlots);
          if (!timeSlotsAreAvailable) {
            dispatchClearMessages(infoTypes.NO_AVAILABLE_TIME_SLOTS);
          }
        });
    }
  }

  componentDidUpdate(prevProps) {
    const {
      dispatchClearMessages,
      dispatchPushMessage,
      dispatchCountedApiCallFinished,
      dispatchRemoveSingleErrorMessage,
      selectedDate: newSelectedDate,
    } = this.props;

    const updateTimeSlots = newSelectedDate && prevProps.selectedDate !== newSelectedDate;
    if (updateTimeSlots) {
      dispatchRemoveSingleErrorMessage(errorTypes.SLOT_TIME_UNAVAILABLE_ERROR);
      dispatchCountedApiCallFinished(false);
      this.loadTimes(newSelectedDate)
        .then((appointmentTypeSlots) => {
          this.setState(() => ({
            appointmentTypeSlots,
          }));
          dispatchCountedApiCallFinished(true);
          return appointmentTypeSlots;
        })
        .then((appointmentTypeSlots) => {
          const timeSlotsAreAvailable = this.checkTimeSlotsAvailability(appointmentTypeSlots);
          if (timeSlotsAreAvailable) {
            dispatchClearMessages(infoTypes.NO_AVAILABLE_TIME_SLOTS);
            this.setState(() => ({
              timeSlotsAreAvailable: true,
            }));
          } else {
            dispatchPushMessage({
              id: getRandomNumber(0, 1000),
              type: infoTypes.NO_AVAILABLE_TIME_SLOTS,
            });
            this.setState(() => ({
              timeSlotsAreAvailable: false,
            }));
          }
        });
    }
  }

  componentWillUnmount() {
    this.resetSelectedDate();
  }

  loadTimes = (date) => {
    const { shopId, appointmentTypes, partnerId } = this.props;
    return timeSlotsApi
      .getTimeSlots(
        shopId,
        appointmentTypes.map((appt) => appt.id),
        dayjs(date).format('YYYY-MM-DD'),
        partnerId || null,
      )
      .catch(this.handleAvailabilityError)
      .then((response) =>
        response.appointmentTypeSlots.reduce((acc, appointmentType) => {
          const appointmentTypeDuration = appointmentTypes.find(
            (appt) => appt.id === appointmentType.appointmentTypeId,
          ).duration;
          acc[appointmentType.appointmentTypeId] = appointmentType.slots.map((slot) => ({
            appointmentTypeId: appointmentType.appointmentTypeId,
            eventId: '',
            from: slot.from,
            to: slot.from + appointmentTypeDuration,
          }));
          return acc;
        }, {}),
      );
  };

  checkTimeSlotsAvailability = (timeSlots) => {
    const availableTimeSlots = Object.values(timeSlots).reduce((acc, curr) => acc.concat(curr), []);
    return availableTimeSlots.length > 0;
  };

  resetSelectedDate = () => {
    const { dispatchSetDateSlot, dispatchClearMessages } = this.props;
    const { timeSlotsAreAvailable } = this.state;
    if (!timeSlotsAreAvailable) {
      dispatchSetDateSlot('');
      dispatchClearMessages(infoTypes.NO_AVAILABLE_TIME_SLOTS);
    }
  };

  handleAvailabilityError = () => ({ appointmentTypeSlots: [] });

  buildAppointmentType = (appointmentType) => {
    const { appointmentTypeSlots } = this.state;
    return { ...appointmentType, slots: appointmentTypeSlots[appointmentType.appointmentType.id] };
  };

  render() {
    const { appointmentTypeSlots } = this.state;
    const {
      /** these properties are required only for TimeSlots.
       * we need rewrite TimeSlots and avoid redundant properties
       * */
      relatedAppointmentType,
      mainAppointmentType,
      formatTimeSlots,
      timeSlotSectionTitle,
      timeSlotsList,
      dispatchSetRelatedAppointmentTimeSlot,
      dispatchSetMainAppointmentTimeSlot,
      areSlotsEqualFun,
    } = this.props;

    if (!appointmentTypeSlots[mainAppointmentType.appointmentType.id]?.length) {
      return null;
    }

    return (
      <TimeSlots
        mainAppointmentType={this.buildAppointmentType(mainAppointmentType)}
        relatedAppointmentType={
          relatedAppointmentType ? this.buildAppointmentType(relatedAppointmentType) : null
        }
        dispatchSetMainAppointmentTimeSlot={dispatchSetMainAppointmentTimeSlot}
        dispatchSetRelatedAppointmentTimeSlot={dispatchSetRelatedAppointmentTimeSlot}
        timeSlotSectionTitle={timeSlotSectionTitle}
        areSlotsEqual={areSlotsEqualFun}
        timeSlotsList={timeSlotsList}
        formatSlots={formatTimeSlots}
      >
        <Messages forTypes={[errorTypes.SLOT_TIME_NOT_SELECTED_ERROR]} />
      </TimeSlots>
    );
  }
}

export const mapStateToProps = (state) => {
  const mainAppointmentType = appointmentDraftSelectors.selectAppointmentType(state);
  const isMultipleAppointment = appointmentDraftSelectors.isMultipleAppointment(state);
  const relatedAppointmentType =
    appointmentDraftSelectors.selectRelatedAppointmentTypeById()(state);

  const selectedDate = appointmentDraftSelectors.selectAppointmentDateSlot(state);
  return {
    selectedDate,
    shopId: appointmentDraftSelectors.selectShop(state).id,
    partnerId: appointmentDraftSelectors.selectPartnerId(state),
    appointmentTypes: appointmentDraftSelectors.selectAllAppointmentTypes(state),
    /** these properties are required only for TimeSlots.
     * we need rewrite TimeSlots and avoid redundant properties
     * */
    mainAppointmentType: {
      appointmentType: mainAppointmentType,
      selectedTimeSlot: appointmentDraftSelectors.selectSelectedTimeSlotById(
        mainAppointmentType.id,
      )(state),
      timeSlotAccordionTitle: isMultipleAppointment ? 'First treatment' : undefined,
    },

    relatedAppointmentType: isMultipleAppointment
      ? {
          appointmentType: relatedAppointmentType,
          selectedTimeSlot: appointmentDraftSelectors.selectSelectedTimeSlotById(
            relatedAppointmentType.id,
          )(state),
          timeSlotAccordionTitle: 'Second treatment',
        }
      : null,
    timeSlotSectionTitle: envConfigSelectors.selectConfig(mainAppointmentType.id)(state).config
      ?.Calendar?.timeSlotSectionTitle,
    timeSlotsList: envConfigSelectors.selectConfig(mainAppointmentType.id)(state).config?.Calendar
      ?.timeSlotsList,
    formatTimeSlots: envConfigSelectors.selectConfig(mainAppointmentType.id)(state).config?.Calendar
      ?.formatTimeSlots,
    areSlotsEqualFun: servicesSelectors.selectCalendarConfig(state).areSlotsEqual || areSlotsEqual,
  };
};

const mapDispatchToProps = {
  dispatchCountedApiCallFinished: loadingSpinnerActions.countedApiCallFinished,
  dispatchClearMessages: messagesActions.clearMessages,
  dispatchPushMessage: messagesActions.pushMessage,
  dispatchSetDateSlot: appointmentDraftActions.setDateSlot,
  dispatchRemoveSingleErrorMessage: errorMessagesActions.removeSingleErrorMessage,

  /** these properties are required only for TimeSlots.
   * we need rewrite TimeSlots and avoid redundant properties
   * */
  dispatchSetMainAppointmentTimeSlot: appointmentDraftActions.setTimeSlot,
  dispatchSetRelatedAppointmentTimeSlot: appointmentDraftActions.setRelatedAppointmentTimeSlot,
};

export const ConnectedTimeSlotsContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(TimeSlotsContainer);

TimeSlotsContainer.propTypes = {
  selectedDate: PropTypes.string,
  shopId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  appointmentTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      duration: PropTypes.number.isRequired,
    }).isRequired,
  ).isRequired,

  dispatchCountedApiCallFinished: PropTypes.func.isRequired,

  /** these properties are required only for TimeSlots.
   * we need rewrite TimeSlots and avoid redundant properties
   * */
  mainAppointmentType: PropTypes.shape({
    appointmentType: PropTypes.shape({
      id: PropTypes.string.isRequired,
      duration: PropTypes.number,
    }).isRequired,
    selectedTimeSlot: PropTypes.shape({
      from: PropTypes.number.isRequired,
      to: PropTypes.number,
    }),
    timeSlotAccordionTitle: PropTypes.string,
  }).isRequired,
  relatedAppointmentType: PropTypes.shape({
    appointmentType: PropTypes.shape({
      id: PropTypes.string.isRequired,
      duration: PropTypes.number,
    }).isRequired,
    selectedTimeSlot: PropTypes.shape({
      from: PropTypes.number.isRequired,
      to: PropTypes.number,
    }),
    timeSlotAccordionTitle: PropTypes.string.isRequired,
  }),
  timeSlotsList: PropTypes.func,
  timeSlotSectionTitle: PropTypes.string,
  formatTimeSlots: PropTypes.func,
  areSlotsEqualFun: PropTypes.func,

  dispatchSetMainAppointmentTimeSlot: PropTypes.func.isRequired,
  dispatchSetRelatedAppointmentTimeSlot: PropTypes.func.isRequired,
  dispatchClearMessages: PropTypes.func.isRequired,
  dispatchPushMessage: PropTypes.func.isRequired,
  dispatchSetDateSlot: PropTypes.func.isRequired,
  dispatchRemoveSingleErrorMessage: PropTypes.func.isRequired,
};

TimeSlotsContainer.defaultProps = {
  selectedDate: undefined,
  partnerId: undefined,

  /** these properties are required only for TimeSlots.
   * we need rewrite TimeSlots and avoid redundant properties
   * */
  relatedAppointmentType: null,
  timeSlotsList: undefined,
  timeSlotSectionTitle: undefined,
  formatTimeSlots: undefined,
  areSlotsEqualFun: undefined,
};
